import { useState } from "react";
import {
    Grid,
    TextField,
    Button,
    Card,
    CardContent,
    Stack,
    FormHelperText,
    FormControl,
    Tooltip,
    Typography
} from "@mui/material";
import { useAtom } from "jotai";
import { Controller, useFieldArray } from "react-hook-form";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { enUS } from "date-fns/locale";
import DateFrequencySelector from "./DateFrequencySelector";
import CustomTimePicker from "../user/components/CustomTimePicker";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { ProviderLookup } from "../components/ProviderLookup";
import InfoTooltip from "../utils/UI/InfoTooltip";
import { Box } from "@mui/system";
import {
    allActiveLocationsAtom,
    confirmationFunctionAtom,
    conformationDialogContentAtom,
    conformationDialogOpenAtom,
    conformationDialogSubmitButtonAtom,
    conformationDialogTitleAtom
} from "../atoms/atoms";
import { validateDateRangeWithInterval } from "../utils/courseUtils";
import { addMonths } from "date-fns";
import { clickedCourseScheduleBoxIndexAtom } from "../atoms/course";
import { format } from "date-fns-tz";
const getDefaultStartTime = () => {
    const date = new Date();
    date.setHours(9, 0, 0, 0);
    return date.getTime();
};

const CoursesrRuleSection = ({
    control,
    setValue,
    getValues,
    sectionIdx,
    scheduleId,
    formState,
    companyId,
    endDateHasPassed,
    handleDeleteDateRange,
    setShowCourseDatesModal
}) => {
    const [openRRuleStartDate, setOpenRRuleStartDate] = useState(false);
    const [openRRuleEndDate, setOpenRRuleEndDate] = useState(false);
    const [anchorElRRule, setAnchorElRRule] = useState(null);
    const [allActiveLocations] = useAtom(allActiveLocationsAtom);
    const [, setConfirmationDialogContent] = useAtom(
        conformationDialogContentAtom
    );
    const [, setConfirmationFunction] = useAtom(confirmationFunctionAtom);
    const [, setConfirmationDialogOpen] = useAtom(conformationDialogOpenAtom);
    const [, setConformationDialogTitle] = useAtom(conformationDialogTitleAtom);
    const [, setConformationDialogSubmitButton] = useAtom(
        conformationDialogSubmitButtonAtom
    );
    const errors = formState?.errors || {};
    const [, setClickedCourseScheduleBoxIndex] = useAtom(
        clickedCourseScheduleBoxIndexAtom
    );
    const numberSelectorPath = `schedules.${sectionIdx}.numberSelector`;
    const startDatePath = `schedules.${sectionIdx}.rruleStartDate`;
    const endDatePath = `schedules.${sectionIdx}.rruleEndDate`;
    const numberSelector = getValues(numberSelectorPath) || {};
    const selectedDays = numberSelector.selectedDays || [];
    const rruleStartDate = getValues(startDatePath);
    const disableRruleChange = getValues(
        `schedules.${sectionIdx}.disableRruleChange`
    );
    const boxCount = getValues("schedules")?.length || 1;

    if (selectedDays.length === 0 && rruleStartDate) {
        const dayOfWeek = new Date(rruleStartDate).getDay();
        setValue(numberSelectorPath, {
            ...numberSelector,
            selectedDays: [dayOfWeek]
        });
    }
    let timeZoneShortDisplay = "";

    if (Array.isArray(getValues("locations")) && allActiveLocations) {
        const locObj = allActiveLocations?.find(
            (loc) => loc.id === getValues("locations")[0]
        );
        if (locObj) {
            const zzz = format(rruleStartDate, "zzz", {
                timeZone: locObj.timezone
            });

            timeZoneShortDisplay = zzz ? `(${zzz})` : "";
        }
    }

    // UseFieldArray for rruleTimeBlocks in the current schedule
    const {
        fields: rruleTimeBlocks,
        append: appendRRuleTimeBlock,
        remove: removeRRuleTimeBlock
    } = useFieldArray({
        control,
        name: `schedules.${sectionIdx}.rruleTimeBlocks`
    });
    const handleProviderSelected = (provider, index) => {
        setValue(
            `schedules.${sectionIdx}.rruleTimeBlocks.${index}.providerInfo`,
            provider?.id
                ? {
                      id: provider.id,
                      firstname: provider.firstname,
                      lastname: provider.lastname,
                      emailaddress: provider.emailaddress,
                      name: `${provider.firstname} ${provider.lastname}`,
                      timezone: provider.timezone
                  }
                : { id: "", name: "" },
            { shouldDirty: true }
        );
    };

    return (
        <Card variant="outlined" sx={{ borderColor: "black" }}>
            <CardContent sx={{ padding: "0px 16px 16px 16px!important" }}>
                <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={{
                        ...enUS,
                        options: { ...enUS.options, weekStartsOn: 1 }
                    }}
                >
                    <Grid container spacing={2.25}>
                        <Grid item xs={6}>
                            <Controller
                                name={`schedules.${sectionIdx}.rruleStartDate`}
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <DatePicker
                                        disabled={disableRruleChange}
                                        autoFocus
                                        id={`schedules.${sectionIdx}-rrule-start-date`}
                                        name={`schedules.${sectionIdx}.rruleStartDate`}
                                        size="small"
                                        label="Start date"
                                        value={value}
                                        fullWidth
                                        inputFormat="MMM d, yyyy"
                                        onChange={(newValue) => {
                                            onChange(newValue);
                                            if (
                                                newValue >
                                                getValues(
                                                    `schedules.${sectionIdx}.rruleEndDate`
                                                )
                                            ) {
                                                setValue(
                                                    `schedules.${sectionIdx}.rruleEndDate`,
                                                    addMonths(newValue, 1)
                                                );
                                            }
                                            const numberSelector =
                                                getValues(numberSelectorPath);
                                            const dayOfWeek = new Date(
                                                newValue
                                            ).getDay();
                                            if (
                                                !numberSelector.selectedDays.includes(
                                                    dayOfWeek
                                                )
                                            ) {
                                                const updatedDays = [
                                                    ...numberSelector.selectedDays,
                                                    dayOfWeek
                                                ];
                                                updatedDays.sort(
                                                    (a, b) => a - b
                                                );
                                                const interval =
                                                    validateDateRangeWithInterval(
                                                        newValue,
                                                        getValues(
                                                            `schedules.${sectionIdx}.rruleEndDate`
                                                        ),
                                                        numberSelector.selectedNumber
                                                    );
                                                setValue(numberSelectorPath, {
                                                    ...numberSelector,
                                                    selectedDays: updatedDays,
                                                    selectedNumber: interval
                                                });
                                            }
                                        }}
                                        open={openRRuleStartDate}
                                        onOpen={() =>
                                            setOpenRRuleStartDate(true)
                                        }
                                        onClose={() => {
                                            setOpenRRuleStartDate(false);
                                            setAnchorElRRule(null);
                                        }}
                                        PopperProps={{
                                            anchorEl: anchorElRRule,
                                            disablePortal: false
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                inputProps={{
                                                    ...params.inputProps,
                                                    readOnly: true
                                                }}
                                                onClick={(e) => {
                                                    setAnchorElRRule(
                                                        e.currentTarget
                                                    );
                                                    setOpenRRuleStartDate(true);
                                                }}
                                                sx={{
                                                    "& .MuiInputAdornment-root":
                                                        { display: "none" },
                                                    "& fieldset": {
                                                        "& legend": {
                                                            width: "54px"
                                                        }
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Controller
                                name={`schedules.${sectionIdx}.rruleEndDate`}
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <DatePicker
                                        disabled={disableRruleChange}
                                        id={`schedules.${sectionIdx}-rrule-end-date`}
                                        name={`schedules.${sectionIdx}.rruleEndDate`}
                                        size="small"
                                        label="End date"
                                        value={value}
                                        minDate={getValues(
                                            `schedules.${sectionIdx}.rruleStartDate`
                                        )}
                                        fullWidth
                                        inputFormat="MMM d, yyyy"
                                        onChange={(newValue) => {
                                            onChange(newValue);
                                            const numberSelector =
                                                getValues(numberSelectorPath);
                                            const interval =
                                                validateDateRangeWithInterval(
                                                    getValues(
                                                        `schedules.${sectionIdx}.rruleStartDate`
                                                    ),
                                                    newValue,

                                                    numberSelector.selectedNumber
                                                );
                                            setValue(numberSelectorPath, {
                                                ...numberSelector,
                                                selectedNumber: interval
                                            });
                                        }}
                                        open={openRRuleEndDate}
                                        onOpen={() => setOpenRRuleEndDate(true)}
                                        onClose={() =>
                                            setOpenRRuleEndDate(false)
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                inputProps={{
                                                    ...params.inputProps,
                                                    readOnly: true
                                                }}
                                                onClick={() =>
                                                    setOpenRRuleEndDate(true)
                                                }
                                                sx={{
                                                    "& .MuiInputAdornment-root":
                                                        { display: "none" },
                                                    "& fieldset": {
                                                        "& legend": {
                                                            width: "50px"
                                                        }
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid
                            container
                            item
                            xs={12}
                            sm={12}
                            sx={{
                                alignItems: "center",
                                justifyContent: "space-between"
                            }}
                        >
                            <Controller
                                name={`schedules.${sectionIdx}.numberSelector`}
                                control={control}
                                rules={{
                                    required:
                                        "Please select at least one day to repeat on"
                                }}
                                render={({
                                    field: {
                                        onChange: onChangeFormHook,
                                        value: {
                                            selectedDays = [],
                                            selectedNumber = 1
                                        } = {}
                                    }
                                }) => (
                                    <DateFrequencySelector
                                        showToolTip={false}
                                        handleNumberSelectorChange={({
                                            target: { value }
                                        }) => {
                                            const interval =
                                                validateDateRangeWithInterval(
                                                    getValues(startDatePath),
                                                    getValues(endDatePath),
                                                    value
                                                );
                                            onChangeFormHook({
                                                selectedDays,
                                                selectedNumber: interval
                                            });
                                        }}
                                        handleDayButtonClick={(day) => {
                                            const startDate =
                                                getValues(startDatePath);
                                            if (
                                                day ===
                                                new Date(startDate).getDay()
                                            )
                                                return; // always keep the day of the start date 'selected'
                                            if (
                                                selectedDays.length === 1 &&
                                                selectedDays[0] === day
                                            ) {
                                                return;
                                            }
                                            const updatedDays =
                                                selectedDays.includes(day)
                                                    ? selectedDays.filter(
                                                          (d) => d !== day
                                                      )
                                                    : [...selectedDays, day];
                                            updatedDays.sort((a, b) => a - b);
                                            onChangeFormHook({
                                                selectedDays: updatedDays,
                                                selectedNumber
                                            });
                                        }}
                                        selectedDays={selectedDays}
                                        selectedNumber={selectedNumber}
                                        disabled={disableRruleChange}
                                    />
                                )}
                            />
                            {!!errors?.schedules?.[sectionIdx]
                                ?.numberSelector && (
                                <FormHelperText error>
                                    {
                                        errors.schedules[sectionIdx]
                                            ?.numberSelector?.message
                                    }
                                </FormHelperText>
                            )}
                        </Grid>

                        <Grid
                            container
                            item
                            xs={12}
                            sm={12}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "16px"
                            }}
                        >
                            {rruleTimeBlocks.map((item, index) => (
                                <Stack
                                    key={item.sequenceId || index}
                                    sx={{ width: "100%" }}
                                >
                                    <Grid item xs={12} sm={6}>
                                        <Controller
                                            name={`schedules.${sectionIdx}.rruleTimeBlocks.${index}.startTime`}
                                            control={control}
                                            render={({ field }) => {
                                                const {
                                                    onChange: onChangeFormHook,
                                                    value: startTime
                                                } = field;
                                                return (
                                                    <CustomTimePicker
                                                        id="start-time"
                                                        name={`schedules.${sectionIdx}.rruleTimeBlocks.${index}.startTime`}
                                                        type="startTime"
                                                        onChange={(
                                                            timestamp
                                                        ) => {
                                                            onChangeFormHook(
                                                                timestamp
                                                            );
                                                        }}
                                                        value={
                                                            startTime ||
                                                            getDefaultStartTime()
                                                        }
                                                        disabled={
                                                            item.usedSeats > 0
                                                        }
                                                        timeZoneShortDisplay={
                                                            timeZoneShortDisplay
                                                        }
                                                    />
                                                );
                                            }}
                                        />
                                    </Grid>

                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        display={"flex"}
                                        alignItems={"center"}
                                        gap={"10px"}
                                    >
                                        <Tooltip
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        width: "200px"
                                                    }
                                                }
                                            }}
                                            disableInteractive
                                            title={
                                                item.usedSeats > 0 &&
                                                "This start time already has bookings on it"
                                            }
                                        >
                                            <FormControl fullWidth>
                                                <Controller
                                                    name={`schedules.${sectionIdx}.rruleTimeBlocks.${index}.providerInfo`}
                                                    control={control}
                                                    rules={{
                                                        required:
                                                            "Select a provider"
                                                    }}
                                                    render={({ field }) => {
                                                        return (
                                                            <ProviderLookup
                                                                companyId={
                                                                    companyId
                                                                }
                                                                showAdornments={
                                                                    false
                                                                }
                                                                initialValue={
                                                                    item.providerInfo
                                                                }
                                                                setSelectedProvider={(
                                                                    u
                                                                ) => {
                                                                    handleProviderSelected(
                                                                        u,
                                                                        index
                                                                    );
                                                                }}
                                                                disabled={
                                                                    item.usedSeats >
                                                                    0
                                                                }
                                                                label="Provider"
                                                            />
                                                        );
                                                    }}
                                                />
                                            </FormControl>
                                        </Tooltip>
                                        {index > 0 && (
                                            <DeleteIcon
                                                type="button"
                                                variant="deleteIcon"
                                                disabled={item.usedSeats > 0}
                                                onClick={() => {
                                                    if (!(item.usedSeats > 0))
                                                        rruleTimeBlocks.length >
                                                            1 &&
                                                            removeRRuleTimeBlock(
                                                                index
                                                            );
                                                }}
                                            />
                                        )}
                                    </Grid>
                                </Stack>
                            ))}
                            <Grid container item>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between", // This spreads items to left and right
                                        width: "100%", // Ensure full width for proper spacing
                                        gap: "18px"
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "12px"
                                        }}
                                    >
                                        <Button
                                            variant="cancelButton"
                                            startIcon={<AddIcon />}
                                            onClick={() => {
                                                if (rruleTimeBlocks?.length) {
                                                    const currentValues =
                                                        getValues(
                                                            `schedules.${sectionIdx}.rruleTimeBlocks`
                                                        );
                                                    const last =
                                                        currentValues[
                                                            currentValues.length -
                                                                1
                                                        ];
                                                    appendRRuleTimeBlock({
                                                        startTime:
                                                            last.startTime +
                                                            60 * 60 * 1000
                                                    });
                                                } else
                                                    appendRRuleTimeBlock({
                                                        startTime:
                                                            getDefaultStartTime()
                                                    });
                                            }}
                                        >
                                            Add start time
                                        </Button>
                                        <InfoTooltip title="Teach the same material at different times on the same day, e.g. group 1 in the morning and group 2 in the afternoon; each start time can have its own provider assignment" />
                                    </Box>
                                    <Button
                                        onClick={() => {
                                            setClickedCourseScheduleBoxIndex(
                                                sectionIdx
                                            );
                                            setShowCourseDatesModal(true);
                                        }}
                                        variant="text"
                                        color="primary"
                                        sx={{
                                            "&:hover": {
                                                backgroundColor:
                                                    "transparent !important"
                                            }
                                        }}
                                        disableRipple
                                    >
                                        <Typography
                                            variant="button"
                                            sx={{
                                                textTransform: "none",
                                                lineHeight: "14px"
                                            }}
                                        >
                                            Verify dates
                                        </Typography>
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                        {boxCount > 1 && sectionIdx > 0 && (
                            <Grid
                                container
                                item
                                xs={12}
                                sm={12}
                                sx={{
                                    paddingTop: "27px !important"
                                }}
                            >
                                <Button
                                    onClick={() => {
                                        setConfirmationFunction(() =>
                                            handleDeleteDateRange(
                                                scheduleId,
                                                sectionIdx
                                            )
                                        );
                                        setConfirmationDialogContent(
                                            <Typography>
                                                Are you sure you want to delete
                                                this date range?
                                            </Typography>
                                        );
                                        setConformationDialogTitle(
                                            "Delete date range"
                                        );
                                        setConformationDialogSubmitButton(
                                            "Delete"
                                        );
                                        setConfirmationDialogOpen(true);
                                    }}
                                    variant="text"
                                    disabled={disableRruleChange}
                                    color="error"
                                    sx={{
                                        "&:hover": {
                                            backgroundColor:
                                                "transparent !important"
                                        }
                                    }}
                                    disableRipple
                                >
                                    <Typography
                                        variant="button"
                                        sx={{
                                            textTransform: "none",
                                            lineHeight: "14px"
                                        }}
                                    >
                                        {`Delete date range`}
                                    </Typography>
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                </LocalizationProvider>
            </CardContent>
        </Card>
    );
};

export default CoursesrRuleSection;
